import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { SearchRequest } from '@mango-engineering/maknet-client-sdk/lib/shop/search/types';
import { COUNTRY } from '@mango-engineering/maknet-client-sdk/lib/shop/types';
import { DeliverySlotItem } from '@makro-shopping/price-calculation-service';
import { config } from '../config/env';
import { DELIVERY_SLOT_FETCH_INTERVAL } from '../constants/common';
import { getRealtimeDeliverySlot } from '../services/deliverySlot/deliverySlot.service';
import { getAfcStoresByStoreCode } from '../services/location/location.service';
import { isReactNativeWebView } from '../services/sendBrowserEvent.service';
import { RecommendationBanner, SearchFilter } from '../types/common';
import { MultiStore } from '../types/multiStore.types';
import { PromoAdvice } from '../types/product.types';
import { getFeatureMinVersion, isFeatureEnabled } from '../utils/common.util';
import { FALLBACK_APP_VERSION, WEBVIEW_MESSAGE_TYPE, defaultSearchRequest } from '../utils/constants';
import { checkPlatform, getUserDetailsFromUrl, isFlagEnabledForCountry } from '../utils/helper';
import appSdkClient from '../utils/sdk';


interface FeatureFlags {
  [key: string]: boolean | string | number;
}

interface UserDetails {
  [key: string]: string | number | boolean | object;
}

interface CarouselModel {
  bannerData?: RecommendationBanner[];
  [key: string]: string | number | boolean | object | undefined;
}

interface RM3Promotion {
  rm3Promotions: Record<string, { promoAdvice: PromoAdvice[]; loading: boolean }>;
}

export const StaticDataContext = createContext({
  featureFlags: {} as FeatureFlags,
  userDetails: {} as UserDetails,
  userSegments: undefined as string[] | undefined,
  isHorecaMember: false,
  carouselModel: {} as CarouselModel,
  setUserDetails: (() => {}) as React.Dispatch<React.SetStateAction<UserDetails>>,
  countryCode: '' as COUNTRY | '',
  setCountryCode: (() => {}) as React.Dispatch<React.SetStateAction<COUNTRY | ''>>,
  currentStoreCodes: [] as string[],
  setCurrentStoreCodes: (() => {}) as React.Dispatch<React.SetStateAction<string[]>>,
  expressStoreCodes: [] as string[],
  setExpressStoreCodes: (() => {}) as React.Dispatch<React.SetStateAction<string[]>>,
  afcStoreCodes: [] as string[],
  setAfcStoreCodes: (() => {}) as React.Dispatch<React.SetStateAction<string[]>>,
  isAfcStoreEnabled: false,
  setIsAfcStoreEnabled: (() => {}) as React.Dispatch<React.SetStateAction<boolean>>,
  appMode: '' as string,
  mmid: '' as string,
  setMmid: (() => {}) as React.Dispatch<React.SetStateAction<string>>,
  alcoholExpiryDate: '' as string,
  setAlcoholExpiryDate: (() => {}) as React.Dispatch<React.SetStateAction<string>>,
  isNative: false,
  setIsNative: (() => {}) as React.Dispatch<React.SetStateAction<boolean>>,
  appVersion: '' as string,
  setAppVersion: (() => {}) as React.Dispatch<React.SetStateAction<string>>,
});

export const DynamicDataContext = createContext({
  searchFilter: {} as SearchFilter,
  searchRequest: {} as SearchRequest,
  complexPromotion: {} as RM3Promotion['rm3Promotions'],
  setComplexPromotion: (() => {}) as React.Dispatch<React.SetStateAction<RM3Promotion['rm3Promotions']>>,
  isExpressDelivery: false,
  setIsExpressDelivery: (() => {}) as React.Dispatch<React.SetStateAction<boolean>>,
  realtimeDeliverySlotData: [] as DeliverySlotItem[] | null,
});

export const DataContext = createContext({
  searchFilter: {} as SearchFilter,
  featureFlags: {} as FeatureFlags,
  userDetails: {} as UserDetails,
  userSegments: undefined as string[] | undefined,
  isHorecaMember: false,
  searchRequest: {} as SearchRequest,
  carouselModel: {} as CarouselModel,
  setUserDetails: (() => {}) as React.Dispatch<React.SetStateAction<UserDetails>>,
  countryCode: '' as COUNTRY | '',
  setCountryCode: (() => {}) as React.Dispatch<React.SetStateAction<COUNTRY | ''>>,
  currentStoreCodes: [] as string[],
  setCurrentStoreCodes: (() => {}) as React.Dispatch<React.SetStateAction<string[]>>,
  afcStoreCodes: [] as string[],
  setAfcStoreCodes: (() => {}) as React.Dispatch<React.SetStateAction<string[]>>,
  isAfcStoreEnabled: false,
  setIsAfcStoreEnabled: (() => {}) as React.Dispatch<React.SetStateAction<boolean>>,
  complexPromotion: {} as RM3Promotion['rm3Promotions'],
  setComplexPromotion: (() => {}) as React.Dispatch<React.SetStateAction<RM3Promotion['rm3Promotions']>>,
  appMode: '' as string,
  isExpressDelivery: false,
  setIsExpressDelivery: (() => {}) as React.Dispatch<React.SetStateAction<boolean>>,
  mmid: '' as string,
  setMmid: (() => {}) as React.Dispatch<React.SetStateAction<string>>,
  alcoholExpiryDate: '' as string,
  setAlcoholExpiryDate: (() => {}) as React.Dispatch<React.SetStateAction<string>>,
  isNative: false,
  setIsNative: (() => {}) as React.Dispatch<React.SetStateAction<boolean>>,
  appVersion: '' as string,
  setAppVersion: (() => {}) as React.Dispatch<React.SetStateAction<string>>,
});

interface DataProviderInterface {
  children: React.ReactNode;
}

export const DataProvider: React.FC<DataProviderInterface> = ({ children }) => {
  const [searchFilterState, setSearchFilterState] = useState<SearchFilter>({});
  const [featureFlagsState, setFeatureFlagsState] = useState<FeatureFlags>({});
  const [userDetailsState, setUserDetailsState] = useState<UserDetails>({});
  const [userSegmentsFromUrl] = useState<string[] | undefined>(() => getUserDetailsFromUrl().userSegments);
  const [isHorecaMemberFromUrl] = useState<boolean>(() => getUserDetailsFromUrl().isHorecaMember);
  const [searchRequestState, setSearchRequestState] = useState<SearchRequest>(defaultSearchRequest);
  const [carouselModelState, setCarouselModelState] = useState<CarouselModel>({});
  const [appMode, setAppMode] = useState<string>('');
  const [countryCode, setCountryCode] = useState<COUNTRY | ''>('');
  const [currentStoreCodes, setCurrentStoreCodes] = useState<string[]>([]);
  const [expressStoreCodes, setExpressStoreCodes] = useState<string[]>([]);
  const [afcStoreCodes, setAfcStoreCodes] = useState<string[]>([]);
  const [isAfcStoreEnabled, setIsAfcStoreEnabled] = useState(false);
  const [isExpressDelivery, setIsExpressDelivery] = useState<boolean>(false);
  const [mmid, setMmid] = useState<string>('');
  const [alcoholExpiryDate, setAlcoholExpiryDate] = useState<string>('');
  const [complexPromotion, setComplexPromotion] = useState<RM3Promotion['rm3Promotions']>({});
  const [isNative, setIsNative] = useState<boolean>(false);
  const [appVersion, setAppVersion] = useState<string>('');
  const [realtimeDeliverySlotData, setRealtimeDeliverySlotData] = useState<DeliverySlotItem[] | null>(null);
  const realtimeDeliverySlotDataRef = useRef<DeliverySlotItem[] | null>(null);
  const isRealtimeDeliverySlotFetchInFlight = useRef(false);
  const lastRealtimeDeliverySlotFetchAt = useRef(0);
  const lastRealtimeDeliveryStoreCodeRef = useRef<string | null>(null);
  const latestStoreCodeRef = useRef<string | null>(null);
  const wasDocumentBackgroundedRef = useRef(false);

  const enableRealtimeDeliverySlotConfig = config.ENABLE_REALTIME_DELIVERY_SLOT;
  const enableHorizonProductCardConfig = config.ENABLE_HORIZON_PRODUCT_CARD;
  const enableMultiDropConfig = config.ENABLE_MULTI_DROP;

  const isRealtimeDeliverySlotEnabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, enableRealtimeDeliverySlotConfig));
  }, [countryCode, enableRealtimeDeliverySlotConfig]);

  const isHorizonUiUxEnabled = useMemo(() => {
    const horizonUiUxMinAppVersion = getFeatureMinVersion(countryCode, config.HORIZON_UI_UX_MIN_VERSION);

    return Boolean(
      isNative &&
        isFlagEnabledForCountry(countryCode, enableHorizonProductCardConfig) &&
        isFeatureEnabled(horizonUiUxMinAppVersion, appVersion || FALLBACK_APP_VERSION),
    );
  }, [appVersion, countryCode, enableHorizonProductCardConfig, isNative]);

  const canFetchRealtimeDeliverySlot = isRealtimeDeliverySlotEnabled && isHorizonUiUxEnabled;
  const isMultiDropEnabled = useMemo(
    () => isFlagEnabledForCountry(countryCode, enableMultiDropConfig),
    [countryCode, enableMultiDropConfig],
  );

  const resetAfcStoreData = useCallback(() => {
    setIsAfcStoreEnabled(false);
    setAfcStoreCodes([]);
  }, []);

  const fetchStoreInfo = useCallback(async () => {
    if (!countryCode || !isMultiDropEnabled || currentStoreCodes.length === 0) {
      resetAfcStoreData();
      return;
    }

    const storeId = currentStoreCodes[0];

    try {
      let storeInfo: Array<Pick<MultiStore, 'afcEnabled' | 'afcStoreCodes'>>;

      if (countryCode === COUNTRY.THAILAND) {
        storeInfo = (await appSdkClient.shop.stores.getAllStoresByStoreIdsRestApi([storeId])) as MultiStore[];
      } else {
        storeInfo = await getAfcStoresByStoreCode({ countryCode, storeCodes: [storeId] });
      }

      if (storeInfo.length === 0) {
        resetAfcStoreData();
        return;
      }

      const store = storeInfo[0];
      setIsAfcStoreEnabled(store.afcEnabled ?? false);
      setAfcStoreCodes(Array.isArray(store.afcStoreCodes) ? store.afcStoreCodes.map(String) : []);
    } catch (error: unknown) {
      resetAfcStoreData();
      const message = error instanceof Error ? error.message : String(error);
      console.error('Error in fetching store info.', message);
    }
  }, [countryCode, currentStoreCodes, isMultiDropEnabled, resetAfcStoreData]);

  useEffect(() => {
    void fetchStoreInfo();
  }, [fetchStoreInfo]);

  const fetchRealtimeDeliverySlotData = useCallback(
    async (storeCode: string, options: { ignoreFreshness?: boolean } = {}) => {
      const now = Date.now();
      const isSameStore = lastRealtimeDeliveryStoreCodeRef.current === storeCode;
      const isFresh = now - lastRealtimeDeliverySlotFetchAt.current < DELIVERY_SLOT_FETCH_INTERVAL;

      if (!canFetchRealtimeDeliverySlot) return realtimeDeliverySlotDataRef.current;
      if (isRealtimeDeliverySlotFetchInFlight.current) return realtimeDeliverySlotDataRef.current;
      if (!options.ignoreFreshness && isSameStore && isFresh) return realtimeDeliverySlotDataRef.current;

      isRealtimeDeliverySlotFetchInFlight.current = true;
      lastRealtimeDeliverySlotFetchAt.current = now;
      lastRealtimeDeliveryStoreCodeRef.current = storeCode;

      try {
        const response = await getRealtimeDeliverySlot({
          storeCode,
          countryCode,
          idToken: userDetailsState?.token as string,
        });
        const nextRealtimeDeliverySlotData = response?.data ?? null;
        realtimeDeliverySlotDataRef.current = nextRealtimeDeliverySlotData;
        setRealtimeDeliverySlotData(nextRealtimeDeliverySlotData);
        return nextRealtimeDeliverySlotData;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('Failed to fetch realtime delivery slot data:', message);
        realtimeDeliverySlotDataRef.current = null;
        setRealtimeDeliverySlotData(null);
        return null;
      } finally {
        isRealtimeDeliverySlotFetchInFlight.current = false;
      }
    },
    [canFetchRealtimeDeliverySlot, countryCode, userDetailsState?.token],
  );

  useEffect(() => {
    latestStoreCodeRef.current = currentStoreCodes?.[0] || null;
  }, [currentStoreCodes]);

  useEffect(() => {
    const activeStoreCode = currentStoreCodes?.[0];
    if (!activeStoreCode) return;

    void fetchRealtimeDeliverySlotData(activeStoreCode);
  }, [currentStoreCodes, fetchRealtimeDeliverySlotData]);

  useEffect(() => {
    const handleAppBackground = () => {
      wasDocumentBackgroundedRef.current = true;
    };

    const handleAppResume = () => {
      if (document.hidden) {
        handleAppBackground();
        return;
      }

      if (!wasDocumentBackgroundedRef.current) return;

      wasDocumentBackgroundedRef.current = false;
      const activeStoreCode = latestStoreCodeRef.current;
      if (activeStoreCode) {
        void fetchRealtimeDeliverySlotData(activeStoreCode);
      }
    };

    document.addEventListener('visibilitychange', handleAppResume);
    window.addEventListener('blur', handleAppBackground);
    window.addEventListener('focus', handleAppResume);

    return () => {
      document.removeEventListener('visibilitychange', handleAppResume);
      window.removeEventListener('blur', handleAppBackground);
      window.removeEventListener('focus', handleAppResume);
    };
  }, [fetchRealtimeDeliverySlotData]);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      let data;
      try {
        data = JSON.parse(event.data);
        if (!data) return;
      } catch {
        if (event?.data?.type) {
          data = event.data;
        } else return;
      }
      const { type, message } = data;
      switch (type) {
        case WEBVIEW_MESSAGE_TYPE.filterApplied:
          setSearchFilterState(message);
          break;

        case WEBVIEW_MESSAGE_TYPE.featureFlag:
          setFeatureFlagsState((prev) => ({ ...prev, ...JSON.parse(message) }));
          break;

        case WEBVIEW_MESSAGE_TYPE.getUserDetails:
          setUserDetailsState(message);
          break;
        case WEBVIEW_MESSAGE_TYPE.searchRequest:
          setSearchRequestState(JSON.parse(message));
          break;
        case WEBVIEW_MESSAGE_TYPE.carouselModel:
          setCarouselModelState(JSON.parse(message));
          break;
        case WEBVIEW_MESSAGE_TYPE.REFRESH_BASKET:
        case WEBVIEW_MESSAGE_TYPE.basketItems:
          // Handled directly in BasketContextProvider (DataProvider is a parent of BasketContextProvider
          // so useBasket() here returns the default context — a no-op)
          break;
        case WEBVIEW_MESSAGE_TYPE.APP_MODE:
          setAppMode(message);
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('DataProvider: Error handling message', error);
    }
  }, []);

  useEffect(() => {
    const isInIframe = window && window.self !== window.top;
    const receiver = checkPlatform() === 'android' && !isInIframe ? document : window;
    if (window !== undefined) {
      if (!isReactNativeWebView()) {
        window.parent.postMessage({ type: 'IFRAME_READY' }, config.MAKRO_WEB_APP_URL);
      }
    }
    receiver.addEventListener('message', handleMessage as EventListener);
    return () => {
      receiver.removeEventListener('message', handleMessage as EventListener);
    };
  }, [handleMessage]);

  const staticValue = useMemo(
    () => ({
      featureFlags: featureFlagsState,
      userDetails: userDetailsState,
      userSegments: userSegmentsFromUrl,
      isHorecaMember: isHorecaMemberFromUrl,
      carouselModel: carouselModelState,
      setUserDetails: setUserDetailsState,
      countryCode,
      setCountryCode,
      currentStoreCodes,
      setCurrentStoreCodes,
      expressStoreCodes,
      setExpressStoreCodes,
      afcStoreCodes,
      setAfcStoreCodes,
      isAfcStoreEnabled,
      setIsAfcStoreEnabled,
      appMode: appMode,
      mmid,
      setMmid,
      alcoholExpiryDate,
      setAlcoholExpiryDate,
      isNative,
      setIsNative,
      appVersion,
      setAppVersion,
    }),
    [
      featureFlagsState,
      userDetailsState,
      userSegmentsFromUrl,
      isHorecaMemberFromUrl,
      carouselModelState,
      countryCode,
      currentStoreCodes,
      expressStoreCodes,
      afcStoreCodes,
      isAfcStoreEnabled,
      appMode,
      mmid,
      alcoholExpiryDate,
      isNative,
      appVersion,
    ],
  );

  const dynamicValue = useMemo(
    () => ({
      searchFilter: searchFilterState,
      searchRequest: searchRequestState,
      complexPromotion: complexPromotion,
      setComplexPromotion: setComplexPromotion,
      isExpressDelivery,
      setIsExpressDelivery,
      realtimeDeliverySlotData,
    }),
    [searchFilterState, searchRequestState, complexPromotion, isExpressDelivery, realtimeDeliverySlotData],
  );

  const mergedValue = useMemo(
    () => ({
      ...staticValue,
      ...dynamicValue,
    }),
    [staticValue, dynamicValue],
  );

  return (
    <DataContext.Provider value={mergedValue}>
      <StaticDataContext.Provider value={staticValue}>
        <DynamicDataContext.Provider value={dynamicValue}>{children}</DynamicDataContext.Provider>
      </StaticDataContext.Provider>
    </DataContext.Provider>
  );
};

export const useStaticData = () => {
  return useContext(StaticDataContext);
};

export const useDynamicData = () => {
  return useContext(DynamicDataContext);
};

export const useData = () => {
  return useContext(DataContext);
};
